import { SyntheticEvent, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { UseFormRegister } from 'react-hook-form';

import { DatePicker, DateTools, Input, Select } from '@treezor/design-system';

import { AdditonalDataLayout } from '@/features/user/components/CreateUser/layout/AddtionalData';
import { CreateUserEmptyComponent } from '@/features/user/components/CreateUser/layout/CreateUserEmptyComponent';
import { CustomColumns, Section, TwoColumns } from '@/features/user/components/CreateUser/layout/ContentLayout';
import { useFieldOptions } from '@/features/user/hooks/createUser/useFieldOptions';
import { useCreateBusinessUser } from '@/features/user/hooks/createUser/useCreateBusinessUser';
import { ICreateUserInfo, IPostCreateUser } from '@/features/user/contexts/CreateUserContext';

import { minimumDate } from '@/hooks/useDashboardConstants';

interface IBirthAndNationalityProps {
  memberIndex: number;
  memberWatch: ICreateUserInfo;
  handleChange: (memberIndex: number, key: keyof ICreateUserInfo, value: string) => void;
  register: UseFormRegister<IPostCreateUser>;
}

export function BirthAndNationality({
  memberIndex,
  memberWatch,
  handleChange: handleChangeMember,
  register,
}: IBirthAndNationalityProps) {
  const { t } = useTranslation();

  const { countryCodeOptions } = useFieldOptions();

  const { language } = useCreateBusinessUser();

  const memberKey = `members.${memberIndex}` as const;

  const handleChange = useCallback(
    (field: keyof ICreateUserInfo, value: string | SyntheticEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const _value = typeof value === 'string' ? value : value?.currentTarget?.value;
      handleChangeMember(memberIndex, field, _value);
    },
    [handleChangeMember, memberIndex],
  );

  const selectedDate = DateTools.localStringToDate(memberWatch.birthday, language);

  return (
    <Section
      title={t('pages.dashboard.createUser.form.blocks.birthNationality')}
      key={`birth-nationality-${memberIndex}`}
    >
      <CustomColumns columns={[1, 2]}>
        <DatePicker
          selectedDate={selectedDate}
          label={t('pages.dashboard.createUser.form.birthday')}
          todayButtonLabel={t('pages.dashboard.createUser.form.actions.today')}
          clearButtonLabel={t('pages.dashboard.createUser.form.actions.clear')}
          onChangeDate={(date) =>
            handleChange('birthday', date ? DateTools.dateToLocalDateString(date, language) : undefined)
          }
          language={language}
          minDate={minimumDate}
          error={
            DateTools.compare(new Date(), selectedDate) === 1
              ? t('pages.dashboard.createUser.form.rules.invalidDate')
              : undefined
          }
        />
        <Select
          label={t('pages.dashboard.createUser.form.birthCountry')}
          options={countryCodeOptions}
          withSearch
          defaultValue={memberWatch?.birthCountry}
          noOptionsComponent={<CreateUserEmptyComponent />}
          onChange={(value: string) => handleChange('birthCountry', value)}
        />
      </CustomColumns>
      <TwoColumns>
        <Input
          type="text"
          label={t('pages.dashboard.createUser.form.placeOfBirth')}
          {...register(`${memberKey}.placeOfBirth`)}
          onChange={(event: SyntheticEvent<HTMLInputElement | HTMLTextAreaElement>) =>
            handleChange('placeOfBirth', event)
          }
        />
        <Select
          label={t('pages.dashboard.createUser.form.nationality')}
          options={countryCodeOptions}
          withSearch
          defaultValue={memberWatch?.nationality}
          noOptionsComponent={<CreateUserEmptyComponent />}
          onChange={(value: string) => handleChange('nationality', value)}
        />
      </TwoColumns>

      <AdditonalDataLayout
        shouldShow={!!memberWatch?.nationalityOther}
        label={t('pages.dashboard.createUser.form.actions.addOtherNationality')}
      >
        <Select
          label={t('pages.dashboard.createUser.form.nationalityOther')}
          options={countryCodeOptions}
          withSearch
          defaultValue={memberWatch?.nationalityOther}
          noOptionsComponent={<CreateUserEmptyComponent />}
          onChange={(value: string) => handleChange('nationalityOther', value)}
        />
      </AdditonalDataLayout>
    </Section>
  );
}
